import React, { useCallback, useEffect, useMemo, useState, memo } from 'react';
import { Tree, Spin, Typography } from 'antd';
import {
  FolderOutlined,
  FolderOpenOutlined,
  RightOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { CustomService } from '@core/services/custom.service';
import { IDirectoryTree } from '@core/@models/DirectoryServiceModel';
import { DataNode, EventDataNode } from 'antd/lib/tree';
import { Key } from 'antd/lib/table/interface';
import { finalize } from 'rxjs/operators';
import { isArray } from 'lodash';

const { Text } = Typography;

interface DirectoryTreeProps {
  onSelect: (folderId: number | null, path: string) => void;
  selectedPath?: string;
  /** When set, only folders with this permission type are shown (e.g. READ_WRITE for upload) */
  permissionFilter?: 'READ_WRITE';
}

const SEPARATOR = '/';

const DirectoryTreeIcon: React.FC<{ expanded?: boolean }> = ({ expanded }) =>
  expanded ? (
    <FolderOpenOutlined style={{ color: '#faad14' }} />
  ) : (
    <FolderOutlined style={{ color: '#faad14' }} />
  );
DirectoryTreeIcon.displayName = 'DirectoryTreeIcon';

const DirectoryTreeComponent: React.FC<DirectoryTreeProps> = ({
  onSelect,
  selectedPath,
  permissionFilter,
}) => {
  const [treeData, setTreeData] = useState<IDirectoryTree[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState<Key>('');
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);
  const [, setLoadingKeys] = useState<Set<Key>>(new Set());

  useEffect(() => {
    fetchRootFolders();
  }, [permissionFilter]);

  const fetchRootFolders = () => {
    setLoading(true);
    CustomService.getData<IDirectoryTree[]>('/file-services/expand')
      .pipe(finalize(() => setLoading(false)))
      .subscribe({
        next: (data) => {
          if (!data || !isArray(data)) return;
          const filtered = permissionFilter
            ? data.filter((d) => d.permissionType === permissionFilter)
            : data;
          const nodes = addRelativeKey({ data: filtered });
          setTreeData(nodes);
        },
      });
  };

  const fetchChildFolders = (folderId: number, key: Key): Promise<void> => {
    setLoadingKeys((prev) => new Set(prev).add(key));
    return new Promise((resolve) => {
      CustomService.getData<IDirectoryTree[]>(
        `/file-services/expand?folderId=${folderId}`
      ).subscribe({
        next: (data) => {
          if (!data || !isArray(data)) {
            setTreeData((origin) => updateTreeData(origin, key, []));
            setExpandedKeys((prev) => prev.filter((k) => k !== key));
            resolve();
            return;
          }
          const filtered = permissionFilter
            ? data.filter((d) => d.permissionType === permissionFilter)
            : data;
          const nodes = addRelativeKey({ parentKey: key, data: filtered });
          setTreeData((origin) => updateTreeData(origin, key, nodes));
          resolve();
        },
        error: () => {
          setTreeData((origin) => updateTreeData(origin, key, []));
          setExpandedKeys((prev) => prev.filter((k) => k !== key));
          resolve();
        },
        complete: () => {
          setLoadingKeys((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        },
      });
    });
  };

  const addRelativeKey = ({
    parentKey,
    data,
  }: {
    parentKey?: Key;
    data: IDirectoryTree[];
  }): IDirectoryTree[] => {
    if (!isArray(data)) return [];
    return data.map((d) => ({
      ...d,
      title: d.name,
      key: parentKey ? `${parentKey}-${d.id}` : `${d.id}`,
      isLeaf: false,
    }));
  };

  const updateTreeData = (
    list: IDirectoryTree[],
    key: Key,
    children: DataNode[]
  ): IDirectoryTree[] => {
    return list.map((node) => {
      if (node.key === key) {
        return {
          ...node,
          children: children.length > 0 ? children : [],
          // Keep isLeaf false so arrow stays visible (avoids switcher disappearing bug)
          isLeaf: false,
        };
      }
      if (Array.isArray(node.children)) {
        return {
          ...node,
          children: updateTreeData(
            node.children as IDirectoryTree[],
            key,
            children
          ),
        };
      }
      return node;
    });
  };

  const getCurrentPath = useCallback(
    (key: Key): string => {
      const traverseKey = key.toString().split('-');
      let currentList: IDirectoryTree[] = [...treeData];
      const pathParts: string[] = [];

      for (const keyPart of traverseKey) {
        const node = currentList.find((v) => `${v.id}` === keyPart);
        if (!node) break;
        pathParts.push(node.name);
        currentList = (node.children as IDirectoryTree[]) || [];
      }

      return SEPARATOR + pathParts.join(SEPARATOR);
    },
    [treeData]
  );

  const onLoadData = useCallback(
    ({ key, children, ...rest }: EventDataNode): Promise<void> => {
      const { id: folderId } = rest as IDirectoryTree;
      if (children) {
        return Promise.resolve();
      }
      return fetchChildFolders(folderId, key);
    },
    []
  );

  const handleSelect = useCallback(
    (selectedKeys: React.Key[]) => {
      const key = selectedKeys[0];
      if (!key) {
        setSelectedKey('');
        onSelect(null, '');
        return;
      }

      setSelectedKey(key);

      const keyParts = key.toString().split('-');
      const folderId = parseInt(keyParts[keyParts.length - 1], 10);
      const path = getCurrentPath(key);

      onSelect(folderId, path);
    },
    [getCurrentPath, onSelect]
  );

  const handleExpand = useCallback((keys: Key[]) => {
    setExpandedKeys(keys);
  }, []);

  // Remove leaf keys from expandedKeys to prevent Tree switcher bug
  const sanitizedExpandedKeys = useMemo(() => {
    const findIsLeaf = (
      list: IDirectoryTree[],
      targetKey: Key
    ): boolean | null => {
      for (const node of list) {
        if (node.key === targetKey) return !!node.isLeaf;
        if (Array.isArray(node.children)) {
          const found = findIsLeaf(
            node.children as IDirectoryTree[],
            targetKey
          );
          if (found !== null) return found;
        }
      }
      return null;
    };
    return expandedKeys.filter((k) => {
      const isLeaf = findIsLeaf(treeData, k);
      return isLeaf === null || !isLeaf;
    });
  }, [expandedKeys, treeData]);

  // Build path breadcrumb from selectedPath
  const pathParts = selectedPath
    ? selectedPath.split('/').filter((p) => p.length > 0)
    : [];

  return (
    <div>
      <div className="directory-tree-simple" style={{ position: 'relative' }}>
        <Spin spinning={loading}>
          {treeData.length > 0 ? (
            <Tree.DirectoryTree
              motion={false}
              loadData={onLoadData}
              treeData={treeData}
              onSelect={handleSelect}
              onExpand={handleExpand}
              expandedKeys={sanitizedExpandedKeys}
              selectedKeys={selectedKey ? [selectedKey] : []}
              showLine={false}
              showIcon
              icon={DirectoryTreeIcon}
              switcherIcon={<DownOutlined />}
              blockNode
              style={{ background: 'transparent' }}
            />
          ) : (
            !loading && (
              <div style={{ padding: 20, textAlign: 'center' }}>
                <Text type="secondary">No folders available</Text>
              </div>
            )
          )}
        </Spin>
      </div>

      {/* Selected path - outside the scroll area */}
      {selectedPath && (
        <div
          style={{
            marginTop: 8,
            padding: '6px 10px',
            background: '#f5f5f5',
            borderRadius: 4,
            border: '1px solid #e8e8e8',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            flexWrap: 'wrap',
            fontSize: 12,
          }}
        >
          <FolderOpenOutlined style={{ color: '#faad14', marginRight: 2 }} />
          {pathParts.map((part, i) => (
            <React.Fragment key={i}>
              {i > 0 && (
                <RightOutlined
                  style={{ fontSize: 9, color: '#bbb', margin: '0 2px' }}
                />
              )}
              <span
                style={{
                  fontWeight: i === pathParts.length - 1 ? 500 : 'normal',
                  color: i === pathParts.length - 1 ? '#333' : '#888',
                }}
              >
                {part}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export const DirectoryTree = memo(DirectoryTreeComponent);
DirectoryTree.displayName = 'DirectoryTree';
